// frontend/src/admin/AdminProtectedRoute.jsx
import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth } from "../context/AuthContext";

/**
 * AdminProtectedRoute — gates the operator admin panel.
 *
 *   - While the initial session restore is in flight → loading splash.
 *   - Not authenticated → redirect to /login.
 *   - Authenticated but NOT a superadmin → "Access Denied" screen (no redirect,
 *     so a logged-in non-admin gets a clear explanation rather than a confusing
 *     bounce back to the main app).
 *   - Superadmin → render the admin shell.
 *
 * `user.is_superadmin` comes from GET /api/auth/me (exposed in UserRead).
 */
function AdminProtectedRoute({ children }) {
  const { token, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <p className="text-slate-300 text-sm">Loading…</p>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_superadmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-center px-6">
        <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
        <p className="text-slate-400 max-w-md mb-6">
          You don&apos;t have permission to view the admin panel. This area is
          restricted to platform operators.
        </p>
        <a
          href="/"
          className="text-sm font-medium text-indigo-400 hover:text-indigo-300"
        >
          ← Back to QRFlow
        </a>
      </div>
    );
  }

  return children ?? <Outlet />;
}

AdminProtectedRoute.propTypes = {
  children: PropTypes.node,
};

AdminProtectedRoute.defaultProps = {
  children: undefined,
};

export default AdminProtectedRoute;
